import Box from 'components/Box';
import Text from 'components/Text';
import PayrollReceiptWrapper from 'features/report/pages/ReportPage/EmployeeSalary/Previews/PayrollReceiptWrapper';
import reportSelectors from 'features/report/services/selectors';
import { IStaffPayrollPrintData } from 'features/report/services/types/reportStaff';
import { IParamsReportSalaryStaff } from 'features/report/services/types/review';
import styled from 'styled-components';
import { formatCurrency } from 'utils/formatCurrency';
import storage from 'utils/sessionStorage';

type Props = {
  params: IParamsReportSalaryStaff;
  staffPayrollData?: IStaffPayrollPrintData[];
};
export const StaffClockOutPrint = ({ params, staffPayrollData: staffPayrollDataProps }: Props) => {
  const _staffPayrollData = reportSelectors.getStaffPayrollData();
  const staffPayrollData = staffPayrollDataProps || _staffPayrollData;
  return (
    <PayrollReceiptWrapper params={params}>
      <Box className='center' highlight>
        <Text
          printMode
          variant='CAPTION_2'
          className='title-highlight'
          style={{ fontSize: '16px', fontFamily: 'Poppins', fontWeight: 600 }}
        >
          {staffPayrollData[0]?.staffName}
        </Text>
      </Box>
      <StaffTicketTable data={staffPayrollData[0]} />
    </PayrollReceiptWrapper>
  );
};

type StaffTicketTableProps = {
  data?: IStaffPayrollPrintData;
};

const StaffTicketTable = ({ data }: StaffTicketTableProps) => {
  const isHoursSalary = data?.salaryType === 'HOURS';

  if (isHoursSalary)
    return (
      <Wrapper>
        <TableStyled>
          <thead>
            <tr>
              <th style={{ textAlign: 'start' }}>CLOCK IN</th>
              <th style={{ textAlign: 'end', paddingRight: 8 }}>CLOCK OUT</th>
              <th style={{ textAlign: 'end' }}>HOURS</th>
            </tr>
          </thead>

          <tbody>
            {data?.historyTimeClocks?.map((item, i) => (
              <tr key={i}>
                <td className='start'>{item?.clockInTime}</td>
                <td className='right pr-8'>{item?.clockOutTime}</td>
                <td className='right'>{item?.hourly}</td>
              </tr>
            ))}
          </tbody>
        </TableStyled>
        <Footer>
          <div className='title'>STAFF DAILY</div>
          <div className='station'>POS Stations #{storage.station_number.get()}</div>
        </Footer>
      </Wrapper>
    );

  return (
    <Wrapper>
      <TableStyled>
        <thead>
          <tr>
            <th style={{ textAlign: 'start' }}>TICKET</th>
            <th style={{ textAlign: 'start' }}>SERVICE</th>
            <th>SALE</th>
            <th style={{ textAlign: 'end' }}>TIPS</th>
          </tr>
        </thead>

        <tbody>
          {data?.tickets?.map((item, i) => (
            <tr key={i}>
              <td className='ticket-col'>
                <div className='ticket-badge'>#{item?.ticketNumber}</div>
              </td>
              <td className='start'>{item.itemName}</td>
              <td className='center'>{formatCurrency(item?.saleService - item?.supplyFee)}</td>
              <td className='right'>{formatCurrency(item?.tip)}</td>
            </tr>
          ))}
        </tbody>
      </TableStyled>
      <TotalBox>
        <Box className='info-item'>
          <Text className='label' variant='CONTENT_2'>
            TOTAL SALES
          </Text>
          <Text className='label' variant='CONTENT_2'>
            {formatCurrency(data?.totalSales || 0)}
          </Text>
        </Box>
        <Box className='info-item'>
          <Text className='label' variant='CONTENT_2'>
            TOTAL TIPS
          </Text>
          <Text className='label' variant='CONTENT_2'>
            {formatCurrency(data?.totalTip)}
          </Text>
        </Box>
      </TotalBox>

      <Footer>
        <div className='title'>STAFF DAILY</div>
        <div className='station'>POS Stations #{storage.station_number.get()}</div>
      </Footer>
    </Wrapper>
  );
};

export default StaffTicketTable;

const Wrapper = styled.div`
  width: 100%;
  padding-top: 4px;
`;

const TableStyled = styled.table`
  width: 100%;
  border-collapse: collapse;

  thead {
    th {
      color: #1d2129;
      font-size: 14px;
      font-weight: 600;
      padding-bottom: 4px;
      border-bottom: 1px dashed #232f3e;
    }
  }

  tbody {
    td {
      font-family: 'Poppins' !important;
      color: #1d2129;
      font-size: 14px;
      font-weight: 400;
      padding-top: 4px;
    }
  }

  .ticket-col {
    width: 55px;
  }

  .ticket-badge {
    width: fit-content;
    padding: 2px 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    background: #232f3e;
    color: white;
    font-size: 16px;
    font-weight: 500;
    font-family: 'Poppins' !important;
  }

  .start {
    text-align: start;
  }

  .center {
    text-align: center;
  }

  .right {
    text-align: right;
  }

  .pr-8 {
    padding-right: 8px;
  }
`;

const TotalBox = styled.div`
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;

  .info-item {
    display: flex;
    justify-content: space-between;
    border-bottom: 1px dashed #232F3E;

    &:last-child {
      border-bottom: none;
    }
  }

  .label {
    font-family: 'Poppins' !important;
  }

  .center {
    text-align: center;
  }

  .right {
    text-align: right;
  }

  .pr-8 {
    padding-right: 8px;
  }
`;

const Footer = styled.div`
  margin-top: 16px;
  padding-top: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-top: 1px solid #232f3e;

  .title {
    color: #1d2129;
    font-size: 16px;
    font-weight: 700;
    margin-bottom: 10px;
  }

  .station {
    background: #232f3e;
    color: white;
    padding: 8px 14px;
    font-size: 20px;
    font-weight: 500;
  }
`;
